import React, { useEffect, useState } from 'react';
import { loadOrCreateIdentity } from '../lib/identity';
import { getProfile, getUserPins } from '../lib/metadata';
import { getUserCommunities } from '../lib/communities';
import { exportTransferCode, buildTransferUrl } from '../lib/deviceTransfer';
import { ProfileEditor } from './ProfileEditor';
import { ShopTab } from './ShopTab';
import { COLORS, SPACING } from '../styles/theme';

export function Profile({ onBack, onOpenSettings }: { onBack: () => void; onOpenSettings?: () => void }) {
  const [identity, setIdentity] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [pins, setPins] = useState<any[]>([]);
  const [communities, setCommunities] = useState<any[]>([]);
  const [editing, setEditing] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [syncPassphrase, setSyncPassphrase] = useState('');
  const [syncCode, setSyncCode] = useState('');
  const [syncUrl, setSyncUrl] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncCopied, setSyncCopied] = useState<'code' | 'url' | null>(null);

  useEffect(() => {
    const id = loadOrCreateIdentity();
    setIdentity(id);

    const prof = getProfile();
    setProfile(prof);

    const userPins = getUserPins(id.userId);
    setPins(userPins);

    const userCommunities = getUserCommunities(id.userId);
    setCommunities(userCommunities);
  }, []);

  if (editing) return <ProfileEditor onDone={() => { setEditing(false); setProfile(getProfile()); }} />;

  return (
    <div style={{
      border: `2px outset ${COLORS.highlight}`,
      backgroundColor: COLORS.panel,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{
        backgroundColor: COLORS.accent,
        color: '#fff',
        padding: `${SPACING.xs} ${SPACING.sm}`,
        fontSize: '9px',
        fontWeight: 700,
        borderBottom: `2px solid ${COLORS.highlight}`,
        userSelect: 'none',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>Profile</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
          {onOpenSettings && (
            <button
              onClick={onOpenSettings}
              title="settings"
              style={{
                backgroundColor: 'transparent',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              ⚙ settings
            </button>
          )}
          <button
            onClick={onBack}
            style={{
              backgroundColor: 'transparent',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 700
            }}
          >
            ✕
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', backgroundColor: COLORS.bg, padding: SPACING.lg }}>
        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          marginBottom: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h2 style={{
            margin: `0 0 ${SPACING.md} 0`,
            fontSize: '16px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            {profile?.username || 'User'}
          </h2>
          <p style={{
            margin: 0,
            fontSize: '9px',
            color: COLORS.textMuted,
            fontFamily: 'monospace',
            wordBreak: 'break-all'
          }}>
            ID: {identity?.userId.slice(0, 12)}...
          </p>
          {profile?.bio && (
            <p style={{
              margin: `${SPACING.lg} 0 0 0`,
              fontSize: '10px',
              color: COLORS.text
            }}>
              {profile.bio}
            </p>
          )}
          <button
            onClick={() => setEditing(true)}
            style={{ marginTop: SPACING.md, background: COLORS.accent, color: '#fff', border: 'none', padding: `${SPACING.xs} ${SPACING.sm}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '12px', fontWeight: 'bold' }}
          >
            Edit profile
          </button>
        </div>

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          marginBottom: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.lg} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Activity
          </h3>
          <p style={{ margin: `0 0 ${SPACING.md} 0`, fontSize: '9px', color: COLORS.textMuted }}>
            Pins: {pins.length}
          </p>
          <p style={{ margin: 0, fontSize: '9px', color: COLORS.textMuted }}>
            Communities: {communities.length}
          </p>
        </div>

        {identity && <ShopTab userId={identity.userId} isSelf={true} />}

        {/* Sync to another device */}
        <div style={{ border: `2px outset ${COLORS.highlight}`, padding: SPACING.lg, marginBottom: SPACING.lg, backgroundColor: COLORS.panel }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showSync ? SPACING.md : 0 }}>
            <h3 style={{ margin: 0, fontSize: '12px', fontFamily: 'monospace', fontWeight: 700, fontStyle: 'italic', color: COLORS.accent }}>
              Sync to another device
            </h3>
            <button onClick={() => { setShowSync(s => !s); setSyncCode(''); setSyncUrl(''); setSyncPassphrase(''); }}
              style={{ background: 'none', border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, padding: '2px 8px', cursor: 'pointer', fontFamily: 'monospace', fontSize: '10px' }}>
              {showSync ? 'close' : 'open'}
            </button>
          </div>
          {showSync && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
              <p style={{ margin: 0, fontSize: '9px', color: COLORS.textMuted, lineHeight: 1.5 }}>
                Creates an encrypted transfer code. Enter it on your other device via Settings → Import Account. Your private key never leaves your device unencrypted.
              </p>
              {!syncCode ? (
                <>
                  <input
                    type="password"
                    placeholder="Choose a passphrase"
                    value={syncPassphrase}
                    onChange={e => setSyncPassphrase(e.target.value)}
                    style={{ padding: SPACING.sm, background: '#fff', color: '#000', border: `2px inset ${COLORS.highlight}`, fontFamily: 'monospace', fontSize: '10px' }}
                  />
                  <button
                    onClick={async () => {
                      if (!syncPassphrase.trim()) return;
                      setSyncLoading(true);
                      try {
                        const code = await exportTransferCode(syncPassphrase);
                        const url = buildTransferUrl(code);
                        setSyncCode(code);
                        setSyncUrl(url);
                      } finally {
                        setSyncLoading(false);
                      }
                    }}
                    disabled={!syncPassphrase.trim() || syncLoading}
                    style={{ background: COLORS.accent, color: '#fff', border: 'none', padding: `${SPACING.sm} ${SPACING.md}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, opacity: syncLoading ? 0.6 : 1 }}
                  >
                    {syncLoading ? 'Generating…' : 'Generate transfer code'}
                  </button>
                </>
              ) : (
                <>
                  <p style={{ margin: 0, fontSize: '9px', color: COLORS.accent, fontWeight: 700 }}>
                    ✓ Code generated. Share it with your other device. Expires after use.
                  </p>
                  <div style={{ display: 'flex', gap: SPACING.sm }}>
                    <textarea readOnly value={syncCode} rows={3}
                      style={{ flex: 1, padding: SPACING.sm, background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, fontFamily: 'monospace', fontSize: '8px', resize: 'none', wordBreak: 'break-all' }} />
                    <button onClick={() => { navigator.clipboard.writeText(syncCode); setSyncCopied('code'); setTimeout(() => setSyncCopied(null), 2000); }}
                      style={{ background: COLORS.panel, border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: `0 ${SPACING.sm}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '9px', flexShrink: 0 }}>
                      {syncCopied === 'code' ? '✓' : 'copy'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: SPACING.sm, alignItems: 'center' }}>
                    <span style={{ fontSize: '9px', color: COLORS.textMuted, fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{syncUrl}</span>
                    <button onClick={() => { navigator.clipboard.writeText(syncUrl); setSyncCopied('url'); setTimeout(() => setSyncCopied(null), 2000); }}
                      style={{ background: COLORS.panel, border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: '2px 8px', cursor: 'pointer', fontFamily: 'monospace', fontSize: '9px', flexShrink: 0 }}>
                      {syncCopied === 'url' ? '✓' : 'copy link'}
                    </button>
                  </div>
                  <button onClick={() => { setSyncCode(''); setSyncUrl(''); setSyncPassphrase(''); }}
                    style={{ background: 'none', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontFamily: 'monospace', fontSize: '9px', textDecoration: 'underline', textAlign: 'left', padding: 0 }}>
                    generate new code
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div style={{
          border: `2px outset ${COLORS.highlight}`,
          padding: SPACING.lg,
          backgroundColor: COLORS.panel
        }}>
          <h3 style={{
            margin: `0 0 ${SPACING.lg} 0`,
            fontSize: '12px',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontStyle: 'italic',
            color: COLORS.accent
          }}>
            Your Communities
          </h3>
          {communities.length === 0 ? (
            <p style={{ color: COLORS.textMuted, margin: 0, fontSize: '9px' }}>None</p>
          ) : (
            communities.map(community => (
              <p key={community.id} style={{ margin: `0 0 ${SPACING.sm} 0`, fontSize: '9px', color: COLORS.text }}>
                {community.name}
              </p>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
